import React from 'react';
import { map } from 'underscore';
import classnames from 'classnames';

import { MessageBar } from '@fluentui/react/lib/MessageBar';
import { Loading } from '@bingads-webui-campaign-react/smart-component-primitive';

import { ImageUploadLocal } from './image-upload-local';
import { SelectImagesListViewWithSearch } from './select-images-list-view-with-search';
import { FacebookPhotosListView } from './facebook-photos-list-view';
import { ImageOptionsKey } from './consts';

const getPivotItemOptions = options =>
  map(options, ({
    key,
    text,
    onRenderItemLink = () => null,
  }) => ({
    key,
    text,
    onRenderItemLink: args => (
      <div className="smart-page-image-editor-pivot-item">
        {onRenderItemLink(args)}
      </div>
    ),
  }));

const IMAGE_WIDTH = 200;
const IMAGE_HEIGHT = 160;
const PAGE_HEIGHT = 432;

const imageProps = {
  imageWidth: IMAGE_WIDTH,
  imageHeight: IMAGE_HEIGHT,
  pageHeight: PAGE_HEIGHT,
};

const getDefaultPivotItemRenderForStockImage = ({
  i18n,
  classes,
  onStockImageItemSelectionChanged,
  defaultKeywords,
  itemsPerPage = 8,
  isMultiple = true,
  existingImages,
  itemToUnselect,
  resetItemToUnselect,
}) => props => (
  <div className={classes.editorPivotStyleContentClassName}>
    <SelectImagesListViewWithSearch
      onItemSelectionChanged={onStockImageItemSelectionChanged}
      isMultiple={isMultiple}
      i18n={i18n}
      itemsPerPage={itemsPerPage}
      defaultKeywords={defaultKeywords}
      existingImages={existingImages}
      itemToUnselect={itemToUnselect}
      resetItemToUnselect={resetItemToUnselect}
      {...imageProps}
      {...props}
    />
  </div>
);

const getDefaultPivotItemRenderForFacebook = ({
  i18n,
  classes,
  isFbSupported,
  itemsPerPage,
  gotoSettings,
  onFacebookItemSelectionChanged,
  fbPhotos,
  isMultiple = true,
  isLoadingFbPhotos,
  itemToUnselect,
  resetItemToUnselect,
}) => props => (
  <div className={classnames('facebook-import-gallery-images-content-container', classes.editorPivotStyleContentClassName)}>
    {
      !isFbSupported &&
      <MessageBar
        className={classnames(classes.infoMessageBarClassName, classes.connectFacebookMsg, 'smart-page-gallery-connect-fb-info')}
        isMultiline
      >
        {i18n.getString(_TL_('Connect your Facebook page to add images.'))}
      </MessageBar>
    }
    <br />
    {
      isLoadingFbPhotos
        ? <Loading className={classes.loading} />
        : <FacebookPhotosListView
          gotoSettings={gotoSettings}
          isFbSupported={isFbSupported}
          onItemSelectionChanged={onFacebookItemSelectionChanged}
          items={fbPhotos}
          i18n={i18n}
          isMultiple={isMultiple}
          itemsPerPage={itemsPerPage}
          itemToUnselect={itemToUnselect}
          resetItemToUnselect={resetItemToUnselect}
          {...imageProps}
          {...props}
        />
    }
  </div>
);

export const getImageOptionsForPivot = ({
  imageOptionsConfig,
  i18n,
  onFileSelected,
  uploadedFileInfo,
  itemsPerPage,
  onStockImageItemSelectionChanged,
  isMultiple,
  classes,
  isFbSupported,
  gotoSettings,
  onFacebookItemSelectionChanged,
  fbPhotos,
  isLoadingFbPhotos,
  defaultKeywords,
  existingImages,
  itemToUnselect,
  resetItemToUnselect,
}) => {
  const options = [{
    key: ImageOptionsKey.DEFAULT,
    text: imageOptionsConfig[ImageOptionsKey.DEFAULT].text || _TL_('Stock image'),
    onRenderItemLink: imageOptionsConfig[ImageOptionsKey.DEFAULT].onRenderItemLink
      || getDefaultPivotItemRenderForStockImage({
        i18n,
        classes,
        onStockImageItemSelectionChanged,
        defaultKeywords,
        itemsPerPage,
        isMultiple,
        existingImages,
        itemToUnselect,
        resetItemToUnselect,
      }),
  }, {
    key: ImageOptionsKey.FACEBOOKIMPORT,
    text: imageOptionsConfig[ImageOptionsKey.FACEBOOKIMPORT].text || _TL_('From Facebook'),
    onRenderItemLink: imageOptionsConfig[ImageOptionsKey.FACEBOOKIMPORT].onRenderItemLink
      || getDefaultPivotItemRenderForFacebook({
        i18n,
        classes,
        isFbSupported,
        itemsPerPage,
        gotoSettings,
        onFacebookItemSelectionChanged,
        fbPhotos,
        isMultiple,
        isLoadingFbPhotos,
        itemToUnselect,
        resetItemToUnselect,
      }),
  }, {
    key: ImageOptionsKey.UPLOAD,
    text: _TL_('Upload your own'),
    onRenderItemLink: props => (
      <div className={classes.editorPivotStyleContentClassName}>
        <ImageUploadLocal
          i18n={i18n}
          onFileSelected={onFileSelected}
          label={i18n.getString(_TL_('Upload your own image'))}
          defaultFileName={uploadedFileInfo && uploadedFileInfo.file && uploadedFileInfo.file.name}
          btnClassName={classes.uploadButtonClassName}
          itemToUnselect={itemToUnselect}
          resetItemToUnselect={resetItemToUnselect}
          {...props}
        />
      </div>
    ),
  }];

  return getPivotItemOptions(options);
};
